import Analytics from 'shared_modules/Analytics';

const _Events = {
  TERMINAL: 'Hardware - Payment Terminal',
  PAYMENT: 'Integrated Payment',
  QUICKPAY: 'QuickPay',
  UNSPECIFIED: 'Unspecified',
};

const _TerminalEventTypes = {
  SALE: 'Sale',
  VOID: 'Void',
  REFUND: 'Refund',
};

const _TerminalEventStates = {
  SUCCESS: 'Success',
  FAIL: 'Fail',
};

export default class PaymentAnalytics {
  static get Events() {
    return _Events;
  }

  static get TerminalEventTypes() {
    return _TerminalEventTypes;
  }

  static get TerminalEventStates() {
    return _TerminalEventStates;
  }

  /**
   *  Generates an analytics event describing a terminal interaction.
   *
   * @param {Terminal} terminal   The terminal that performed the transaction
   * @param {string} transactionType  The type of transaction
   * @param {string} status   The status of the transaction
   * @param {TerminalPayment} payment Payment information details
   * @param {string} gatewayType Type of gateway processing the terminal transaction
   */
  static trackTerminalEvent(
    terminal,
    transactionType,
    status = null,
    payment = null,
    gatewayType = PaymentAnalytics.Events.UNSPECIFIED
  ) {
    let eventData = terminal.info();

    if (payment) {
      eventData.salePaymentID = payment.model.id;
      eventData.ccChargeID = payment.model.get('ccChargeID') || PaymentAnalytics.Events.UNSPECIFIED;
      eventData.saleID = payment.model.get('saleID') || PaymentAnalytics.Events.UNSPECIFIED;
      eventData.paymentProcessor = gatewayType;
    }

    if (transactionType) {
      eventData.transactionType = transactionType;
    }

    if (status) {
      eventData.status = status;
    }

    Analytics.track(PaymentAnalytics.Events.TERMINAL, eventData);
  }

  /**
   * Generates an analytics event describing an integrated payment operation.
   *
   * @param {string} event    The specific event that has occurred
   * @param {number|string} saleID    The ID of the sales
   * @param {object}  additionalData   Key/value pairs of data that can be sent along with the event
   */
  static trackPaymentEvent(event, saleID, additionalData = {}) {
    let eventData = additionalData;
    eventData.saleID = saleID || PaymentAnalytics.Events.UNSPECIFIED;

    PaymentAnalytics.trackGenericPaymentEvent(
      PaymentAnalytics.Events.PAYMENT,
      event,
      saleID,
      additionalData
    );
  }

  /**
   * Generates an analytics event describing a generic payment operation.
   *
   * @param {string} eventType    The general event that has occurred (e.g. QuickPay)
   * @param {string} event    The specific event that has occurred
   * @param {number|string} saleID    The ID of the sales
   * @param {object}  additionalData   Key/value pairs of data that can be sent along with the event
   */
  static trackGenericPaymentEvent(eventType, event, saleID, additionalData = {}) {
    let eventData = additionalData;
    eventData.saleID = saleID || PaymentAnalytics.Events.UNSPECIFIED;

    Analytics.track(`${eventType}${event && event.length ? ` - ${event}` : ''}`, eventData);
  }

  /**
   * Generates an analytics event pushed through the dataLayer for Google Tag Manager
   *
   * @param {typeof PaymentAnalytics.TerminalEventTypes[keyof PaymentAnalytics.TerminalEventTypes]} eventType Either SALE, VOID, REFUND
   * @param {typeof PaymentAnalytics.TerminalEventStates[keyof PaymentAnalytics.TerminalEventStates]} eventState Either SUCCESS or FAIL
   */
  static trackPaymentOperationsEventGTM(eventType, eventState) {
    // If the event type is "Void" we return "Refund" having
    // the "Void" event type might be useful in the future
    const modifiedEventType =
      eventType === PaymentAnalytics.TerminalEventTypes.VOID
        ? PaymentAnalytics.TerminalEventTypes.REFUND
        : eventType;

    if (window.dataLayer) {
      window.dataLayer.push({
        event: `${modifiedEventType.toLowerCase()}_terminalPayment_${eventState.toLowerCase()}`,
      });
    }
  }
}
